// MSSQL renderer-side schema introspection for the dialect-native resolver.
// This owns FK edges, user routines (+ parameter signatures) and sequences —
// the table list lives in `mssqlTableCache.ts`, columns/CHECKs in
// `mssqlColumnIntrospection.ts`, indexes in `mssqlIndexIntrospection.ts`.
// FK edges are needed by the orchestrator's FK-driven JOIN snippet (PRD:
// Slice "MSSQL Resolver — Joins + FK").
//
// Composite-key FKs come back as multiple rows (one per
// `constraint_column_id`); we aggregate them by `fk.object_id` so the
// resulting `FkEdge[]` keeps `fromColumns` / `toColumns` in column-ordinal
// order. Routine parameters aggregate the same way, by `o.object_id`.
//
// S7 item 2 — all three artefacts are paged (`OFFSET/FETCH` off the catalog's
// own object_id ordering). Both multi-row artefacts pass a `groupKeyOf` so a
// constraint / routine straddling a page boundary is carried to the next page
// instead of aggregating into two partial items.

import type { FkEdge, RoutineMeta, SequenceMeta } from '@/core/engines/intellisenseTypes';
import type { IntrospectionRow } from './introspectionPaging';
import { fetchMssqlPagedArtefact, type PagedFetchOptions } from './mssqlIntrospectionQuery';

// TOTAL row budgets across all pages (S7 item 4) — memory bounds, not
// per-request caps (the per-page guard is `INTROSPECTION_PAGE_MAX_ROWS`). FK
// and routine rows are one per column / per parameter. See
// `introspectionBudgets.ts`.
import {
  FK_ROW_BUDGET,
  ROUTINE_ROW_BUDGET,
  SEQUENCE_ROW_BUDGET,
} from './introspectionBudgets';

/** Sinks handed one page's worth of aggregated items (S7 item 1's phase 2). */
export type MssqlFkPageSink = (edges: readonly FkEdge[]) => void | Promise<void>;
export type MssqlRoutinePageSink = (routines: readonly RoutineMeta[]) => void | Promise<void>;
export type MssqlSequencePageSink = (sequences: readonly SequenceMeta[]) => void | Promise<void>;

export function buildMssqlForeignKeysPageSql(offsetFetch: string): string {
  return `SELECT
  fk.object_id AS FkObjectId,
  fk.name AS FkName,
  schema_from.name AS FromSchema,
  tab_from.name AS FromTable,
  col_from.name AS FromColumn,
  schema_to.name AS ToSchema,
  tab_to.name AS ToTable,
  col_to.name AS ToColumn,
  fkc.constraint_column_id AS Ord
FROM sys.foreign_keys fk
JOIN sys.foreign_key_columns fkc
  ON fkc.constraint_object_id = fk.object_id
JOIN sys.tables tab_from
  ON tab_from.object_id = fkc.parent_object_id
JOIN sys.schemas schema_from
  ON schema_from.schema_id = tab_from.schema_id
JOIN sys.columns col_from
  ON col_from.object_id = fkc.parent_object_id
  AND col_from.column_id = fkc.parent_column_id
JOIN sys.tables tab_to
  ON tab_to.object_id = fkc.referenced_object_id
JOIN sys.schemas schema_to
  ON schema_to.schema_id = tab_to.schema_id
JOIN sys.columns col_to
  ON col_to.object_id = fkc.referenced_object_id
  AND col_to.column_id = fkc.referenced_column_id
ORDER BY fk.object_id, fkc.constraint_column_id
${offsetFetch}`;
}

export async function fetchMssqlForeignKeyPages(
  connectionId: string,
  database: string,
  sink: MssqlFkPageSink,
  options?: PagedFetchOptions,
): Promise<void> {
  await fetchMssqlPagedArtefact<FkEdge>({
    connectionId,
    database,
    label: 'fks',
    artefact: 'foreignKeys',
    sqlForPage: buildMssqlForeignKeysPageSql,
    aggregate: aggregateFkRows,
    sink,
    totalBudget: FK_ROW_BUDGET,
    groupKeyOf: (row) => String(row.FkObjectId ?? ''),
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating convenience wrapper over `fetchMssqlForeignKeyPages`. */
export async function fetchMssqlForeignKeys(
  connectionId: string,
  database: string,
): Promise<FkEdge[]> {
  const out: FkEdge[] = [];
  await fetchMssqlForeignKeyPages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

/**
 * User routines (stored procedures + scalar/inline/table-valued functions)
 * with their parameter signatures. System-shipped objects are excluded — the
 * resolver's curated `sp_*` list owns those. Parameter rows come back
 * one-per-parameter (LEFT JOIN keeps zero-param routines);
 * `aggregateRoutineRows` groups them into `RoutineMeta`.
 */
export function buildMssqlRoutinesPageSql(offsetFetch: string): string {
  return `SELECT
  o.object_id AS ObjectId,
  s.name AS SchemaName,
  o.name AS RoutineName,
  o.type AS ObjType,
  p.name AS ParamName,
  t.name AS ParamType,
  p.is_output AS IsOutput,
  p.parameter_id AS Ord
FROM sys.objects o
JOIN sys.schemas s ON s.schema_id = o.schema_id
LEFT JOIN sys.parameters p
  ON p.object_id = o.object_id AND p.parameter_id > 0
LEFT JOIN sys.types t ON t.user_type_id = p.user_type_id
WHERE o.type IN ('P', 'FN', 'IF', 'TF')
  AND o.is_ms_shipped = 0
ORDER BY o.object_id, p.parameter_id
${offsetFetch}`;
}

export async function fetchMssqlRoutinePages(
  connectionId: string,
  database: string,
  sink: MssqlRoutinePageSink,
  options?: PagedFetchOptions,
): Promise<void> {
  await fetchMssqlPagedArtefact<RoutineMeta>({
    connectionId,
    database,
    label: 'routines',
    artefact: 'routines',
    sqlForPage: buildMssqlRoutinesPageSql,
    aggregate: aggregateRoutineRows,
    sink,
    totalBudget: ROUTINE_ROW_BUDGET,
    groupKeyOf: (row) => String(row.ObjectId ?? ''),
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating convenience wrapper over `fetchMssqlRoutinePages`. */
export async function fetchMssqlRoutines(
  connectionId: string,
  database: string,
): Promise<RoutineMeta[]> {
  const out: RoutineMeta[] = [];
  await fetchMssqlRoutinePages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

type RoutineRow = {
  readonly ObjectId: unknown;
  readonly SchemaName: unknown;
  readonly RoutineName: unknown;
  readonly ObjType: unknown;
  readonly ParamName: unknown;
  readonly ParamType: unknown;
  readonly IsOutput: unknown;
  readonly Ord: unknown;
};

/**
 * Group per-parameter rows into one `RoutineMeta` per routine. Exported for
 * unit tests (mirrors `aggregateFkRows`). T-SQL object types map to:
 * `P` → procedure; `FN` → scalar function; `IF`/`TF` → function with
 * `returns: 'table'` so callers can tell TVFs apart from scalar UDFs.
 */
export function aggregateRoutineRows(
  rows: readonly IntrospectionRow[],
): RoutineMeta[] {
  type Pending = {
    schema: string;
    name: string;
    kind: RoutineMeta['kind'];
    returns?: string;
    params: { ord: number; name: string; dataType: string; isOutput: boolean }[];
  };
  const byObjectId = new Map<string, Pending>();
  for (const raw of rows) {
    const row = raw as RoutineRow;
    const objectId = String(row.ObjectId ?? '').trim();
    const name = String(row.RoutineName ?? '').trim();
    const objType = String(row.ObjType ?? '').trim().toUpperCase();
    if (!objectId || !name) continue;
    let entry = byObjectId.get(objectId);
    if (!entry) {
      entry = {
        schema: String(row.SchemaName ?? '').trim(),
        name,
        kind: objType === 'P' ? 'procedure' : 'function',
        ...(objType === 'IF' || objType === 'TF' ? { returns: 'table' } : {}),
        params: [],
      };
      byObjectId.set(objectId, entry);
    }
    const paramName = String(row.ParamName ?? '').trim();
    if (paramName) {
      entry.params.push({
        ord: Number(row.Ord ?? 0),
        name: paramName,
        dataType: String(row.ParamType ?? '').trim() || 'unknown',
        isOutput: row.IsOutput === true || row.IsOutput === 1,
      });
    }
  }
  const out: RoutineMeta[] = [];
  for (const entry of byObjectId.values()) {
    entry.params.sort((a, b) => a.ord - b.ord);
    out.push({
      name: entry.name,
      schema: entry.schema || undefined,
      kind: entry.kind,
      parameters: entry.params.map((p) => ({
        name: p.name,
        dataType: p.dataType,
        mode: p.isOutput ? 'out' : 'in',
      })),
      ...(entry.returns ? { returns: entry.returns } : {}),
    });
  }
  return out;
}

type FkRow = {
  readonly FkObjectId: unknown;
  readonly FkName: unknown;
  readonly FromSchema: unknown;
  readonly FromTable: unknown;
  readonly FromColumn: unknown;
  readonly ToSchema: unknown;
  readonly ToTable: unknown;
  readonly ToColumn: unknown;
  readonly Ord: unknown;
};

/**
 * Aggregate per-column rows from `sys.foreign_key_columns` into one
 * `FkEdge` per constraint. Exported so the unit tests can exercise the
 * grouping without standing up the IPC bridge.
 */
export function aggregateFkRows(rows: readonly IntrospectionRow[]): FkEdge[] {
  type Pending = {
    objectId: string;
    name: string;
    fromSchema: string;
    fromTable: string;
    toSchema: string;
    toTable: string;
    fromCols: { ord: number; col: string }[];
    toCols: { ord: number; col: string }[];
  };

  const byObjectId = new Map<string, Pending>();
  for (const raw of rows) {
    const row = raw as FkRow;
    const objectId = String(row.FkObjectId ?? '').trim();
    const fromTable = String(row.FromTable ?? '').trim();
    const toTable = String(row.ToTable ?? '').trim();
    const fromColumn = String(row.FromColumn ?? '').trim();
    const toColumn = String(row.ToColumn ?? '').trim();
    if (!objectId || !fromTable || !toTable || !fromColumn || !toColumn) continue;
    const ord = Number(row.Ord ?? 0);
    let entry = byObjectId.get(objectId);
    if (!entry) {
      entry = {
        objectId,
        name: String(row.FkName ?? '').trim(),
        fromSchema: String(row.FromSchema ?? '').trim(),
        fromTable,
        toSchema: String(row.ToSchema ?? '').trim(),
        toTable,
        fromCols: [],
        toCols: [],
      };
      byObjectId.set(objectId, entry);
    }
    entry.fromCols.push({ ord, col: fromColumn });
    entry.toCols.push({ ord, col: toColumn });
  }

  const out: FkEdge[] = [];
  for (const entry of byObjectId.values()) {
    entry.fromCols.sort((a, b) => a.ord - b.ord);
    entry.toCols.sort((a, b) => a.ord - b.ord);
    out.push({
      name: entry.name || undefined,
      fromSchema: entry.fromSchema || undefined,
      fromTable: entry.fromTable,
      fromColumns: entry.fromCols.map((c) => c.col),
      toSchema: entry.toSchema || undefined,
      toTable: entry.toTable,
      toColumns: entry.toCols.map((c) => c.col),
    });
  }
  return out;
}

/**
 * User sequences (`sys.sequences`) for `NEXT VALUE FOR` target completion.
 * One cheap query per page; failures degrade to whatever pages already
 * landed, mirroring `fetchMssqlRoutines`.
 */
export function buildMssqlSequencesPageSql(offsetFetch: string): string {
  return `SELECT s.name AS SchemaName, seq.name AS SequenceName
FROM sys.sequences seq
JOIN sys.schemas s ON s.schema_id = seq.schema_id
ORDER BY seq.object_id
${offsetFetch}`;
}

export async function fetchMssqlSequencePages(
  connectionId: string,
  database: string,
  sink: MssqlSequencePageSink,
  options?: PagedFetchOptions,
): Promise<void> {
  await fetchMssqlPagedArtefact<SequenceMeta>({
    connectionId,
    database,
    label: 'sequences',
    artefact: 'sequences',
    sqlForPage: buildMssqlSequencesPageSql,
    aggregate: mapSequenceRows,
    sink,
    totalBudget: SEQUENCE_ROW_BUDGET,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating convenience wrapper over `fetchMssqlSequencePages`. */
export async function fetchMssqlSequences(
  connectionId: string,
  database: string,
): Promise<SequenceMeta[]> {
  const out: SequenceMeta[] = [];
  await fetchMssqlSequencePages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

/** Exported for unit tests (mirrors `aggregateRoutineRows`). */
export function mapSequenceRows(
  rows: readonly IntrospectionRow[],
): SequenceMeta[] {
  const out: SequenceMeta[] = [];
  for (const row of rows) {
    const schema = String(row.SchemaName ?? '').trim();
    const name = String(row.SequenceName ?? '').trim();
    if (!name) continue;
    out.push({ name, ...(schema ? { schema } : {}) });
  }
  return out;
}
